/**
 * @fileoverview Defines the Fireworks API client (FireworksApi) and configurations for various models like Llama 3 and Deepseek. Implements streaming chat completions using the `streamChatCompletions` function. Includes definitions for `ModelNeedingKeys` such as `Llama_33_70B_n1_singleLine` and `Deepseek_R1`.
 */

import type { ApiKeys, KeyProvider } from './api_keys'
import type {
  InputTool,
  LlmApi,
  Message,
  ModelNeedingKeys,
  ResponseToolCall,
  Usage,
} from './llm_types'

export const Llama_4_Scout: ModelNeedingKeys = {
  name: 'fireworks-llama-4-scout',
  promptTokenCost: 0.15 / 1000000,
  completionTokenCost: 0.6 / 1000000,
  getApi: (settings: KeyProvider) =>
    new FireworksApi('accounts/fireworks/models/llama4-scout-instruct-basic', settings, 1, false),
}

export const Llama_4_Maverick: ModelNeedingKeys = {
  name: 'fireworks-llama-4-maverick',
  promptTokenCost: 0.22 / 1000000,
  completionTokenCost: 0.88 / 1000000,
  getApi: (settings: KeyProvider) =>
    new FireworksApi(
      'accounts/fireworks/models/llama4-maverick-instruct-basic',
      settings,
      1,
      false,
    ),
}

export const Llama_33_70B_n4_singleLine: ModelNeedingKeys = {
  name: 'fireworks-llama-v3p3-70b-instruct',
  promptTokenCost: 0.9 / 1000000,
  completionTokenCost: 0.9 / 1000000,
  getApi: (settings: KeyProvider) =>
    new FireworksApi('accounts/fireworks/models/llama-v3p3-70b-instruct', settings, 4, true),
}

export const Llama_33_70B_n1_singleLine: ModelNeedingKeys = {
  name: 'fireworks-llama-v3p3-70b-instruct',
  promptTokenCost: 0.9 / 1000000,
  completionTokenCost: 0.9 / 1000000,
  getApi: (settings: KeyProvider) =>
    new FireworksApi('accounts/fireworks/models/llama-v3p3-70b-instruct', settings, 1, true),
}

export const Llama_33_70B_n1_multiLine: ModelNeedingKeys = {
  name: 'fireworks-llama-v3p3-70b-instruct',
  promptTokenCost: 0.9 / 1000000,
  completionTokenCost: 0.9 / 1000000,
  getApi: (settings: KeyProvider) =>
    new FireworksApi('accounts/fireworks/models/llama-v3p3-70b-instruct', settings, 1, false),
}

export const Llama_3_2_3B: ModelNeedingKeys = {
  name: 'fireworks-llama-v3p2-3b-instruct',
  promptTokenCost: 0.1 / 1000000,
  completionTokenCost: 0.1 / 1000000,
  getApi: (settings: KeyProvider) =>
    new FireworksApi('accounts/fireworks/models/llama-v3p2-3b-instruct', settings, 1, false),
}

export const Llama_3_1_8B: ModelNeedingKeys = {
  name: 'fireworks-llama-v3p1-8b-instruct',
  promptTokenCost: 0.2 / 1000000,
  completionTokenCost: 0.2 / 1000000,
  getApi: (settings: KeyProvider) =>
    new FireworksApi('accounts/fireworks/models/llama-v3p1-8b-instruct', settings, 1, false),
}

export const Deepseek_R1: ModelNeedingKeys = {
  name: 'fireworks-deepseek-r1',
  promptTokenCost: 8 / 1000000,
  completionTokenCost: 8 / 1000000,
  getApi: (settings: KeyProvider) =>
    new FireworksApi('accounts/fireworks/models/deepseek-r1', settings, 1, false),
}

export const Deepseek_V3: ModelNeedingKeys = {
  name: 'fireworks-deepseek-v3',
  promptTokenCost: 0.9 / 1000000,
  completionTokenCost: 0.9 / 1000000,
  getApi: (settings: KeyProvider) =>
    new FireworksApi('accounts/fireworks/models/deepseek-v3', settings, 1, false),
}

export class FireworksApi implements LlmApi {
  private apiKey: string | undefined
  requiredKeys: ApiKeys[] = ['FIREWORKS_API_KEY']
  constructor(
    private model: string,
    keyProvider: KeyProvider,
    private n: number = 1,
    private singleLine: boolean = false,
  ) {
    this.apiKey = keyProvider.get('FIREWORKS_API_KEY')
    if (!this.apiKey) {
      throw new Error('FIREWORKS_API_KEY is not set')
    }
  }

  async chatCompletion(
    _system: string | undefined,
    _texts: string[],
    _tools: InputTool[],
    _conversation: Message[],
  ): Promise<{ message: Message; toolCalls: Array<ResponseToolCall>; usage: Usage }> {
    throw new Error('Method not implemented.')
  }

  async streamChatCompletions(
    system: string | undefined,
    texts: string[],
    conversation: Message[],
    handler: (message: any) => void,
    prediction?: string,
  ): Promise<{ message: Message; usage: Usage; inputMessages: Message[] }> {
    const messages = []
    if (system) {
      messages.push({ role: 'system', content: system })
    }
    messages.push(...texts.map((text) => ({ role: 'user', content: text })))
    messages.push(...conversation)

    const payload: any = {
      model: this.model,
      max_tokens: 16384,
      top_p: 1,
      top_k: 40,
      presence_penalty: 0,
      frequency_penalty: 0,
      temperature: 0,
      n: this.n,
      messages,
    }

    if (prediction && this.n === 1) {
      payload.prediction = { type: 'content', content: prediction }
    }

    if (this.singleLine) {
      payload.stop = ['\n']
    }

    const result = await fetch('https://api.fireworks.ai/inference/v1/chat/completions', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify(payload),
    })
    const json = await result.json()
    if (json.error) {
      throw new Error(json.error.message)
    }
    if (handler) {
      handler(json.choices[0].message.content)
    }
    return {
      inputMessages: conversation,
      message: json.choices[0].message,
      usage: {
        promptTokens: json.usage.prompt_tokens,
        completionTokens: json.usage.completion_tokens,
      },
    }
  }
}
